import { useRouter } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import type { CollectionSummary } from '@realmery/realm-core';
import { RealmClientError, realms } from '../db/client.ts';

export const COLLECTION_NAME_MAX = 200;
const NEW = '__new__';

/**
 * The collections an entry is in (docs/ENTRY-EDITOR-SLICE-3-PLAN.md section 3.3), as the
 * desktop's Collections section: chips with ×, and + Add to collection, which also makes a
 * new one. Every change is written at once, and the realm's lists reload with it.
 */
export function EntryCollections({ file, entryId, collections }: {
  file: string;
  entryId: string;
  /** The realm's collections when the editor opened. */
  collections: readonly CollectionSummary[];
}) {
  const router = useRouter();
  const [known, setKnown] = useState(() => collections.map((c) => ({ id: c.id, name: c.name })));
  const [member, setMember] = useState(() => collections.filter((c) => c.entryIds.includes(entryId)).map((c) => c.id));
  const [adding, setAdding] = useState(false);
  const [choice, setChoice] = useState('');
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');
  const [working, setWorking] = useState(false);

  const names = new Map(known.map((c) => [c.id, c.name]));
  const others = known.filter((c) => !member.includes(c.id)).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  const failed = (e: unknown, fallback: string) => setError(e instanceof RealmClientError ? e.message : fallback);

  async function save(next: string[]): Promise<boolean> {
    setWorking(true);
    setError('');
    try {
      await realms.setEntryCollections(file, entryId, next);
      setMember(next);
      void router.invalidate();
      return true;
    } catch (e) {
      failed(e, 'The collections could not be saved.');
      return false;
    } finally {
      setWorking(false);
    }
  }

  async function add(event: FormEvent) {
    event.preventDefault();
    let id = choice;
    if (choice === NEW) {
      const name = newName.trim();
      if (name === '') return;
      try {
        id = (await realms.createCollection(file, name)).id;
      } catch (e) {
        failed(e, 'The collection could not be made.');
        return;
      }
      setKnown((list) => [...list, { id, name }]);
    }
    if (id && (await save([...member, id]))) {
      setAdding(false);
      setChoice('');
      setNewName('');
    }
  }

  return (
    <section className="editor-section" aria-labelledby="collections-heading">
      <h2 id="collections-heading">Collections</h2>
      {member.length === 0 && <p className="meta">Not in any collection yet.</p>}
      <ul className="relationship-chips">
        {member.map((id) => (
          <li key={id} className="relationship-chip">
            {names.get(id) ?? 'Unknown collection'}
            <button
              type="button"
              className="chip-remove"
              disabled={working}
              onClick={() => void save(member.filter((other) => other !== id))}
              aria-label={`Take this entry out of ${names.get(id) ?? 'the collection'}`}
            >
              ×
            </button>
          </li>
        ))}
      </ul>
      {error && (
        <p className="notice-error" role="alert">
          {error}
        </p>
      )}
      {adding ? (
        <form className="panel relationship-form" onSubmit={(event) => void add(event)} aria-labelledby="add-collection-heading">
          <h3 id="add-collection-heading">Add to a collection</h3>
          <label>
            Collection
            <select value={choice} autoFocus onChange={(event) => setChoice(event.currentTarget.value)}>
              <option value="" disabled>
                Choose…
              </option>
              {others.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
              <option value={NEW}>New collection…</option>
            </select>
          </label>
          {choice === NEW && (
            <label>
              Name of the new collection
              <input value={newName} maxLength={COLLECTION_NAME_MAX} autoFocus onChange={(event) => setNewName(event.currentTarget.value)} />
            </label>
          )}
          <div className="actions">
            <button type="submit" className="primary" disabled={working || choice === '' || (choice === NEW && newName.trim() === '')}>
              Add
            </button>
            <button type="button" onClick={() => setAdding(false)} disabled={working}>
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <div className="actions">
          <button type="button" onClick={() => setAdding(true)}>
            + Add to collection
          </button>
        </div>
      )}
    </section>
  );
}
